import React, { useState, useCallback } from 'react';
import { useAuthenticatedPhoto } from '../../hooks/use-authenticated-photo';

type AuthenticatedImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string | undefined;
  fallback?: React.ReactNode;
  onFetchError?: () => void;
  onLoad?: () => void;
};

export const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  src,
  fallback = null,
  alt,
  onFetchError,
  onLoad,
  className,
  style,
  ...rest
}) => {
  const photo = useAuthenticatedPhoto(src);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    onLoad?.();
  }, [onLoad]);

  React.useEffect(() => {
    if (photo.status === 'error' && onFetchError) {
      onFetchError();
    }
  }, [photo.status, onFetchError]);

  React.useEffect(() => {
    setImageLoaded(false);
  }, [src]);

  // If no photo data yet, show only fallback
  if (photo.status === 'idle' || photo.status === 'error') {
    return <>{fallback}</>;
  }

  // Show fallback until image loads
  const isReady = photo.status === 'loaded' && imageLoaded;

  return (
    <>
      {!isReady && fallback}
      <img
        src={photo.status === 'loaded' ? photo.objectUrl : ''}
        alt={alt}
        onLoad={handleImageLoad}
        className={className}
        style={style}
        {...rest}
      />
    </>
  );
};

export default AuthenticatedImage;
